import { LoadingButton } from '@mui/lab'
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  Typography
} from '@mui/material'
import { GreenText } from 'components'
import * as controllers from 'controllers'
import { ImportBranchDto, PermissionDto, PersonVTS2AccessDto } from 'dtos'
import { Formik } from 'formik'
import { useEffect, useState } from 'react'

/** Select value for All Locations; form still persists `defaultImportBranch: null`. */
const ALL_LOCATIONS_SELECT_VALUE = '__ALL_LOCATIONS__'

interface VTS2AccessAddEditDialogProps {
  initialValues: PersonVTS2AccessDto
  loading: boolean
  onClose: () => void
  onSave: (values: any) => void
  open: boolean
}

export default function VTS2AccessAddEditDialog({
  initialValues,
  loading,
  onClose,
  onSave,
  open
}: VTS2AccessAddEditDialogProps) {
  const [isGettingPermissions, setIsGettingPermissions] = useState<boolean>(false)
  const [permissions, setPermissions] = useState<PermissionDto[]>([])
  const [isGettingImportBranches, setIsGettingImportBranches] = useState<boolean>(false)
  const [importBranches, setImportBranches] = useState<ImportBranchDto[]>([])

  useEffect(() => {
    setIsGettingPermissions(true)
    controllers
      .getPermissions()
      .then(response => {
        setPermissions(response.value)
      })
      .finally(() => {
        setIsGettingPermissions(false)
      })
  }, [])

  useEffect(() => {
    if (!open || importBranches.length > 0) {
      return
    }
    setIsGettingImportBranches(true)
    controllers
      .getImportBranches()
      .then(response => {
        setImportBranches(response.value)
      })
      .finally(() => {
        setIsGettingImportBranches(false)
      })
  }, [open, importBranches.length])

  return (
    <Dialog open={open}>
      <Formik
        enableReinitialize={true}
        initialValues={initialValues}
        validateOnChange={true}
        validateOnBlur={false}
        onSubmit={(values, formikHelpers) => {
          onSave(values)
          formikHelpers.setSubmitting(false)
        }}
      >
        {({ errors, handleSubmit, isSubmitting, setFieldTouched, setFieldValue, touched, values }) => {
          const branchSelectValue =
            values.defaultImportBranch?.importBranchGuid != null && values.defaultImportBranch.importBranchGuid !== ''
              ? values.defaultImportBranch.importBranchGuid
              : ALL_LOCATIONS_SELECT_VALUE

          return (
            <form onSubmit={handleSubmit}>
              <DialogContent>
                <Grid container spacing={2} alignItems='center'>
                  <DialogTitle>VTS2 Access</DialogTitle>

                  <Grid item xs={12}>
                    <FormControl
                      disabled={loading || isSubmitting || isGettingImportBranches}
                      error={Boolean(touched.defaultImportBranch && errors.defaultImportBranch)}
                      fullWidth
                      variant='outlined'
                    >
                      <InputLabel id='vts2-default-import-branch-label'>Default Import Branch</InputLabel>
                      <Select
                        labelId='vts2-default-import-branch-label'
                        label='Default Import Branch'
                        value={branchSelectValue}
                        onBlur={() => setFieldTouched('defaultImportBranch', true)}
                        onChange={e => {
                          const v = e.target.value as string
                          if (v === ALL_LOCATIONS_SELECT_VALUE) {
                            setFieldValue('defaultImportBranch', null)
                          } else {
                            const branch = importBranches.find(b => b.importBranchGuid === v)
                            if (branch) {
                              setFieldValue('defaultImportBranch', branch)
                            }
                          }
                        }}
                      >
                        <MenuItem value={ALL_LOCATIONS_SELECT_VALUE}>All Locations</MenuItem>
                        {importBranches.map(branch => (
                          <MenuItem key={branch.importBranchGuid ?? branch.name} value={branch.importBranchGuid ?? ''}>
                            {branch.name}
                          </MenuItem>
                        ))}
                      </Select>
                      {touched.defaultImportBranch && errors.defaultImportBranch ? (
                        <FormHelperText>{String(errors.defaultImportBranch)}</FormHelperText>
                      ) : null}
                    </FormControl>
                  </Grid>

                  {isGettingPermissions && (
                    <Grid item container alignItems='center' justifyContent='center'>
                      <Grid item>
                        <CircularProgress color='primary' size={50} />
                      </Grid>
                    </Grid>
                  )}

                  {!isGettingPermissions &&
                    permissions.map(permission => (
                      <Grid item xs={12} key={permission.permissionGuid}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={values.permissions.some(
                                valuesPermission => valuesPermission.permissionGuid === permission.permissionGuid
                              )}
                              disabled={loading || isSubmitting}
                              onChange={() => {
                                if (
                                  values.permissions.some(
                                    valuesPermission => valuesPermission.permissionGuid === permission.permissionGuid
                                  )
                                ) {
                                  setFieldValue(
                                    'permissions',
                                    values.permissions.filter(
                                      valuesPermission => valuesPermission.permissionGuid !== permission.permissionGuid
                                    )
                                  )
                                } else {
                                  setFieldValue('permissions', [...values.permissions, permission])
                                }
                              }}
                            />
                          }
                          label={
                            <Grid>
                              <Typography>{permission.name}</Typography>

                              <Typography variant='body2' color='#808080'>
                                {permission.description}
                              </Typography>
                            </Grid>
                          }
                        />
                      </Grid>
                    ))}

                  <Grid item xs={12}>
                    <GreenText variant='outlined'>
                      {`**Default Import Branch:**

The “Default Import Branch” defaults to “HQ Location” and is a required field. Dropdown options include “All Locations”, “HQ Location”, “Flatrock Location”, and “Columbus Location”.

When users in VTS2 open the “Select To Do List” dialog, the “Import Branch” dropdown will default to the “Default Import Branch” dropdown selection.

**Administration Add/Edit Switch:**
                      
Grants the ability to manage system settings.

**Cancel Import Capability Switch:**

Allows the user to cancel vehicle imports and delete PAPS within inbound trips.

**Cluster Admin Switch:**

Enables management of Cluster Information (dialog) and Incorrect Mileage 
Resolution (dialog) on the Vehicle Details - Clusters (tab).
Also permits replacing/uploading the "3rd Party Cluster Delivery Photo from 
Cluster Shippers."

**Clusters Add/Edit Switch:**

Allows management of clusters.

**Companies and People Add/Edit Switch:**

Grants the ability to manage companies and people.

**Companies and People Read Only Switch:**

Allows viewing of companies and people without editing permissions.

**Conformity Add/Edit Switch:**

Enables management of conformity-related information.

**Damage Add/Edit Switch:**

Allows the user to manage damage records.

**Inbound Trips Add/Edit Switch:**

Grants permissions to manage inbound trips.

**Invoices Add/Edit Switch:**

Allows management of invoices.

**Label Printing Add/Edit Switch:**

Grants the ability to manage label printing.

**OCR Add/Edit Switch:**

Allows management of OCR (Optical Character Recognition) processes.

**Outbound Trips Add/Edit Switch:**

Grants permissions to manage outbound trips.

**Recall Add/Edit Switch:**

Enables management of vehicle recalls.

**Receives "Bond Block Alert" Emails Switch:**

Notifies the user via email when bond block alerts are triggered.

**Receives "Mileage Discrepancy Notification" Emails Switch:**

Sends email notifications when a converted cluster has a mileage discrepancy.

**Receives "Missing 7501 Document" Emails Switch:**

Notifies the user via email when a missing 7501 document email is sent to the 
broker.

**Receives "Missing CAN Ownership" Emails Switch:**

Sends an email notification when a missing CAN Ownership email is sent to the 
exporter.

**Receives "Missing Label" Emails Switch:**

Notifies the user via email when a vehicle is checked in with missing 
"Manufacturer Label" or "Tire Pressure Label."

**Receives "Missing Ohio Title Alert" Emails Switch:**

Triggers email notifications when the Missing Ohio Title Alert date threshold is 
met.

**Receives "Not Sent to DOT After 5 Days" Emails Switch:**
Notifies the user via email when a vehicle record has not been sent to DOT after 5 
days of creation.

**Receives "Send to Broker" Emails Switch:**

Sends email notifications when documents are sent to the broker.

**Receives "Special Bond Request" Emails Switch:**

Notifies the user via email when a special bond request email is sent to the 
insurance company.

**Receives “Damage Notification” Email Switch:**

Notifies the user via email when a vehicle damage record has been created. 

**Titles Add/Edit Switch:**

Allows the user to manage titles.

**Users Add/Edit Switch:**

Grants permissions to manage VTS2 users.

**Vehicle Check-In Add Switch:**

Enables the user to press the "Check-In" button on the "Needs Check-In" To-Do List 
and complete the vehicle check-in process.

**Vehicle Check-In Edit Switch:**

Allows management of the "Vehicle Details - Vehicle Check-In" tab.`}
                    </GreenText>
                  </Grid>
                </Grid>
              </DialogContent>

              <DialogActions>
                <Button disabled={loading || isSubmitting} color='inherit' onClick={onClose} variant='text'>
                  CANCEL
                </Button>

                <LoadingButton loading={loading || isSubmitting} type='submit' variant='contained'>
                  SAVE
                </LoadingButton>
              </DialogActions>
            </form>
          )
        }}
      </Formik>
    </Dialog>
  )
}
